import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import { FALLBACK_LOCALE, isLocale } from "@offertli/shared";
import de from "./de.js";
import en from "./en.js";
import es from "./es.js";
import fr from "./fr.js";
import { domainTranslations } from "./domain.js";
import { employeeTranslations } from "./employees.js";
import { commercialTranslations } from "./commercial.js";
import { operationsTranslations } from "./operations.js";

type Tree = { [key: string]: string | Tree };

/** Deep-merge translation trees; later sources win per leaf key. */
function merge(...sources: object[]): Tree {
  const result: Tree = {};
  for (const source of sources) {
    for (const [key, value] of Object.entries(source)) {
      const current = result[key];
      result[key] =
        typeof value === "object" && value !== null
          ? merge(typeof current === "object" ? current : {}, value)
          : (value as string);
    }
  }
  return result;
}

const bundle = (
  base: object,
  locale: "en" | "de" | "fr" | "es",
): { translation: Tree } => ({
  translation: merge(
    base,
    domainTranslations[locale],
    employeeTranslations[locale],
    commercialTranslations[locale],
    operationsTranslations[locale],
  ),
});

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: bundle(en, "en"),
      de: bundle(de, "de"),
      fr: bundle(fr, "fr"),
      es: bundle(es, "es"),
    },
    fallbackLng: FALLBACK_LOCALE,
    supportedLngs: ["en", "de", "fr", "es"],
    nonExplicitSupportedLngs: true,
    ns: ["translation"],
    defaultNS: "translation",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "offertli_locale",
    },
  });

/** Switch the UI language; persists via the language detector's localStorage cache. */
export function setLocale(locale: string): void {
  if (isLocale(locale) && i18n.language !== locale) {
    void i18n.changeLanguage(locale);
  }
}

export default i18n;
